import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import { useNotifications } from '@/providers/notificationHooks';
import { useApiErrorMessage } from '@/shared/data/errors';
import { updateTagColor, type TagColor } from '@/shared/data/tags';
import { queryKeys } from '@/shared/hooks';

type UseDetailEditEditorParams<TStatus extends string> = {
  projectId?: string | null;
  successMessage?: string;
  saveEntity: (input: {
    name?: string;
    tags?: string[];
    status?: TStatus;
    timestamp?: Date | null;
    secondaryTimestamp?: Date | null;
    durationMs?: number | null;
  }) => Promise<void>;
};

type UseDetailEditEditorResult<TStatus extends string> = {
  isPending: boolean;
  save: (input: {
    name?: string;
    tags?: string[];
    colors?: TagColor[];
    status?: TStatus;
    timestamp?: Date | null;
    secondaryTimestamp?: Date | null;
    durationMs?: number | null;
  }) => Promise<void>;
};

export const useDetailEditEditor = <TStatus extends string>({
  projectId,
  successMessage = 'Changes saved.',
  saveEntity,
}: UseDetailEditEditorParams<TStatus>): UseDetailEditEditorResult<TStatus> => {
  const queryClient = useQueryClient();
  const { selectedOrganisationId } = useOrganisationFilter();
  const { notify } = useNotifications();
  const toErrorMessage = useApiErrorMessage();

  const saveMutation = useMutation({
    mutationFn: async (input: {
      name?: string;
      tags?: string[];
      colors?: TagColor[];
      status?: TStatus;
      timestamp?: Date | null;
      secondaryTimestamp?: Date | null;
      durationMs?: number | null;
    }) => {
      await saveEntity({
        name: input.name,
        tags: input.tags,
        status: input.status,
        timestamp: input.timestamp,
        secondaryTimestamp: input.secondaryTimestamp,
        durationMs: input.durationMs,
      });
      const resolvedTags = input.tags ?? [];
      const resolvedColors = input.colors ?? [];
      const updates = resolvedTags
        .map((name, index) => ({
          name: name.trim(),
          color: resolvedColors[index] ?? 'neutral',
        }))
        .filter((item) => item.name.length > 0);
      if (updates.length === 0) {
        return;
      }
      await Promise.all(
        updates.map(async (item) => {
          await updateTagColor(item, selectedOrganisationId ?? undefined);
        }),
      );
    },
  });

  const save = useCallback(
    async (input: {
      name?: string;
      tags?: string[];
      colors?: TagColor[];
      status?: TStatus;
      timestamp?: Date | null;
      secondaryTimestamp?: Date | null;
      durationMs?: number | null;
    }) => {
      try {
        await saveMutation.mutateAsync(input);
        await queryClient.invalidateQueries({
          queryKey: queryKeys.tagOptions(projectId ?? null, selectedOrganisationId ?? null),
        });
        await queryClient.refetchQueries({ type: 'active' });
        notify({ message: successMessage, tone: 'green' });
      } catch (error) {
        notify({ message: toErrorMessage(error), tone: 'red' });
        throw error;
      }
    },
    [
      notify,
      projectId,
      queryClient,
      saveMutation,
      selectedOrganisationId,
      successMessage,
      toErrorMessage,
    ],
  );

  return {
    isPending: saveMutation.isPending,
    save,
  };
};
